"use client";
/**
 * Reqrium icon set — hand-rolled inline SVGs (Lucide-style: 24x24 viewBox,
 * stroke-based, rounded caps/joins) so the redesign doesn't need a new icon
 * dependency. Every icon accepts the same `IconProps`, so callers can treat
 * them interchangeably (size, strokeWidth, className, etc.).
 */
import type { SVGProps } from "react";

export type IconProps = SVGProps<SVGSVGElement> & {
  size?: number | string;
  strokeWidth?: number;
};

function base(
  { size = 20, strokeWidth = 1.75, ...props }: IconProps,
  children: React.ReactNode
) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {children}
    </svg>
  );
}

export const SearchIcon = (p: IconProps) =>
  base(p, <><circle cx={11} cy={11} r={7} /><line x1={16.65} y1={16.65} x2={21} y2={21} /></>);

export const BellIcon = (p: IconProps) =>
  base(p, <><path d="M5 17h14l-1.5-2A6 6 0 0 1 17 11V9a5 5 0 0 0-10 0v2a6 6 0 0 1-1.5 4L5 17Z" /><path d="M9 17a3 3 0 0 0 6 0" /></>);

export const MicIcon = (p: IconProps) =>
  base(p, <><rect x={9} y={3} width={6} height={11} rx={3} /><path d="M5 11a7 7 0 0 0 14 0" /><line x1={12} y1={18} x2={12} y2={22} /><line x1={8} y1={22} x2={16} y2={22} /></>);

export const HomeIcon = (p: IconProps) =>
  base(p, <><path d="M3 11.5 12 4l9 7.5" /><path d="M5 10v10h14V10" /><path d="M10 20v-6h4v6" /></>);

export const WalletIcon = (p: IconProps) =>
  base(p, <><path d="M3 7V6a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v1" /><rect x={3} y={7} width={18} height={12} rx={2} /><circle cx={17} cy={13} r={1.4} /></>);

export const PhoneIcon = (p: IconProps) =>
  base(p, <path d="M5 4h3l1.5 4-2 1.5a11 11 0 0 0 5 5l1.5-2 4 1.5v3a1.5 1.5 0 0 1-1.6 1.5A16 16 0 0 1 3.5 5.6 1.5 1.5 0 0 1 5 4Z" />);

export const LinkIcon = (p: IconProps) =>
  base(p, <><path d="M9 17H7a5 5 0 0 1 0-10h2" /><path d="M15 7h2a5 5 0 0 1 0 10h-2" /><line x1={8} y1={12} x2={16} y2={12} /></>);

export const HeartIcon = (p: IconProps) =>
  base(p, <path d="M12 21s-7-4.35-9.5-8.5C.5 8.5 2.5 5 6 5c2 0 3.5 1.5 4 2.5.5-1 2-2.5 4-2.5 3.5 0 5.5 3.5 3.5 7.5C19 16.65 12 21 12 21Z" />);

export const MegaphoneIcon = (p: IconProps) =>
  base(p, <><path d="M9 9 19 5v14L9 15" /><path d="M3 11v2a2 2 0 0 0 2 2h1l3 4v-6" /></>);

export const RssIcon = (p: IconProps) =>
  base(p, <><circle cx={5} cy={19} r={1.4} fill="currentColor" stroke="none" /><path d="M4 11a9 9 0 0 1 9 9" /><path d="M4 5a15 15 0 0 1 15 15" /></>);

export const UserIcon = (p: IconProps) =>
  base(p, <><circle cx={12} cy={8} r={4} /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" /></>);

export const GiftIcon = (p: IconProps) =>
  base(p, <><rect x={3} y={8} width={18} height={13} rx={1} /><path d="M3 8h18v4H3z" /><line x1={12} y1={8} x2={12} y2={21} /><path d="M12 8c-1.5 0-3.2-.9-3.2-2.5S10 3.3 12 5.2c0-1.9 1.7-3.4 3.2-1.7S13.5 8 12 8Z" /></>);

export const TrendingUpIcon = (p: IconProps) =>
  base(p, <><polyline points="3 17 9 11 13 15 21 7" /><polyline points="16 7 21 7 21 12" /></>);

export const ShieldIcon = (p: IconProps) =>
  base(p, <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6Z" />);

export const ShieldAlertIcon = (p: IconProps) =>
  base(p, <><path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6Z" /><line x1={12} y1={8} x2={12} y2={13} /><circle cx={12} cy={16.3} r={0.4} fill="currentColor" stroke="none" /></>);

export const ShieldCheckIcon = (p: IconProps) =>
  base(p, <><path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6Z" /><polyline points="9 12 11 14 15 9" /></>);

export const GlobeIcon = (p: IconProps) =>
  base(p, <><circle cx={12} cy={12} r={9} /><line x1={3} y1={12} x2={21} y2={12} /><path d="M12 3c3 3 3 15 0 18" /><path d="M12 3c-3 3-3 15 0 18" /></>);

export const EyeIcon = (p: IconProps) =>
  base(p, <><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" /><circle cx={12} cy={12} r={3} /></>);

export const ArrowRightIcon = (p: IconProps) =>
  base(p, <><line x1={5} y1={12} x2={19} y2={12} /><polyline points="12 5 19 12 12 19" /></>);

export const ArrowUpRightIcon = (p: IconProps) =>
  base(p, <><line x1={7} y1={17} x2={17} y2={7} /><polyline points="8 7 17 7 17 16" /></>);

export const ChevronDownIcon = (p: IconProps) => base(p, <polyline points="6 9 12 15 18 9" />);
export const ChevronUpIcon = (p: IconProps) => base(p, <polyline points="18 15 12 9 6 15" />);
export const ChevronRightIcon = (p: IconProps) => base(p, <polyline points="9 6 15 12 9 18" />);

export const SettingsIcon = (p: IconProps) =>
  base(
    p,
    <>
      <circle cx={12} cy={12} r={3} />
      <path d="M19.4 13a7.6 7.6 0 0 0 0-2l2-1.5-2-3.4-2.3.8a7.4 7.4 0 0 0-1.7-1l-.4-2.4h-4l-.4 2.4a7.4 7.4 0 0 0-1.7 1l-2.3-.8-2 3.4 2 1.5a7.6 7.6 0 0 0 0 2l-2 1.5 2 3.4 2.3-.8a7.4 7.4 0 0 0 1.7 1l.4 2.4h4l.4-2.4a7.4 7.4 0 0 0 1.7-1l2.3.8 2-3.4Z" />
    </>
  );

export const MenuIcon = (p: IconProps) =>
  base(p, <><line x1={4} y1={7} x2={20} y2={7} /><line x1={4} y1={12} x2={20} y2={12} /><line x1={4} y1={17} x2={20} y2={17} /></>);

export const CloseIcon = (p: IconProps) =>
  base(p, <><line x1={6} y1={6} x2={18} y2={18} /><line x1={18} y1={6} x2={6} y2={18} /></>);

export const CheckCircleIcon = (p: IconProps) =>
  base(p, <><circle cx={12} cy={12} r={9} /><polyline points="8 12 11 15 16 9" /></>);

export const AlertTriangleIcon = (p: IconProps) =>
  base(p, <><path d="M12 3 22 20H2Z" /><line x1={12} y1={9.5} x2={12} y2={14} /><circle cx={12} cy={16.8} r={0.4} fill="currentColor" stroke="none" /></>);

export const ActivityIcon = (p: IconProps) =>
  base(p, <polyline points="3 12 8 12 11 4 14 20 17 12 21 12" />);

export const BarChartIcon = (p: IconProps) =>
  base(p, <><rect x={4} y={10} width={3.2} height={10} /><rect x={10.4} y={6} width={3.2} height={14} /><rect x={16.8} y={3} width={3.2} height={17} /></>);

export const UsersIcon = (p: IconProps) =>
  base(p, <><circle cx={9} cy={8} r={3.2} /><path d="M2.5 20c0-3.4 3-6 6.5-6s6.5 2.6 6.5 6" /><circle cx={17.5} cy={9} r={2.4} /><path d="M14.7 14.3c2.6.4 4.8 2.4 4.8 5.7" /></>);

export const LockIcon = (p: IconProps) =>
  base(p, <><rect x={5} y={11} width={14} height={10} rx={2} /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>);

export const SparklesIcon = (p: IconProps) =>
  base(p, <><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5Z" /><path d="M19 14l.6 1.7 1.7.6-1.7.6-.6 1.7-.6-1.7-1.7-.6 1.7-.6Z" /></>);

export const MapPinIcon = (p: IconProps) =>
  base(p, <><path d="M12 21s7-7.5 7-12a7 7 0 1 0-14 0c0 4.5 7 12 7 12Z" /><circle cx={12} cy={9} r={2.4} /></>);

export const ClockIcon = (p: IconProps) =>
  base(p, <><circle cx={12} cy={12} r={9} /><line x1={12} y1={12} x2={12} y2={7.5} /><line x1={12} y1={12} x2={15.5} y2={14} /></>);

export const ExternalLinkIcon = (p: IconProps) =>
  base(p, <><path d="M14 4h6v6" /><line x1={20} y1={4} x2={10} y2={14} /><path d="M20 14v6H4V4h6" /></>);

export const ZapIcon = (p: IconProps) =>
  base(p, <polygon points="13 2 4 14 11 14 10 22 20 10 13 10" />);

export const PlayIcon = (p: IconProps) =>
  base(p, <polygon points="6 3 20 12 6 21" />);

export const StarIcon = (p: IconProps) =>
  base(p, <path d="M12 2.5l3 6.5 7 .8-5.2 4.6 1.6 6.8L12 17.7 5.6 21.2l1.6-6.8L2 9.8l7-.8Z" />);

export const RocketIcon = (p: IconProps) =>
  base(
    p,
    <>
      <path d="M12 2.2c2.2 1.8 3.5 4.8 3.5 8 0 2.6-1.1 5-3.5 7-2.4-2-3.5-4.4-3.5-7 0-3.2 1.3-6.2 3.5-8Z" />
      <circle cx={12} cy={10} r={1.5} />
      <path d="M8.7 14c-2 .5-3 2-3 4.5 2.5 0 4-1 4.5-3" />
      <path d="M15.3 14c2 .5 3 2 3 4.5-2.5 0-4-1-4.5-3" />
      <path d="M10.3 18.5c0 1.4.7 2.4 1.7 3 1-.6 1.7-1.6 1.7-3" />
    </>
  );

export const RadarIcon = (p: IconProps) =>
  base(
    p,
    <>
      <circle cx={12} cy={12} r={9} />
      <circle cx={12} cy={12} r={5.5} />
      <circle cx={12} cy={12} r={1.6} fill="currentColor" stroke="none" />
      <line x1={12} y1={12} x2={18.5} y2={6.5} />
    </>
  );

export const HandHeartIcon = (p: IconProps) =>
  base(
    p,
    <>
      <path d="M4 13a8 8 0 0 0 13.5 6c1-.9 1.8-2.6 2.3-4.5.3-1.2-1-2.1-2-1.4l-1.4 1" />
      <path d="M12 10.8c-.9-1.8-3.6-1.8-3.6.6 0 1.8 2.2 3.1 3.6 4.3 1.4-1.2 3.6-2.5 3.6-4.3 0-2.4-2.7-2.4-3.6-.6Z" />
    </>
  );

export const CubeIcon = (p: IconProps) =>
  base(
    p,
    <>
      <polygon points="12 2.5 20.5 7 20.5 17 12 21.5 3.5 17 3.5 7" />
      <path d="M12 12 20.5 7" />
      <path d="M12 12 3.5 7" />
      <path d="M12 12 12 21.5" />
    </>
  );

export const FlameIcon = (p: IconProps) =>
  base(
    p,
    <path d="M12 21c4 0 6-2.7 6-6.2 0-2.6-1.4-4.3-2.6-5.7.2 1.7-.7 2.8-1.8 2.8-1.3 0-1.9-1.1-1.5-2.5.7-2.4-1-4.7-2.5-6.4.3 2.3-1.1 3.7-2.1 5.3C6.3 9.9 6 11.4 6 12.8 6 17 8 21 12 21Z" />
  );

export const CopyIcon = (p: IconProps) =>
  base(p, <><rect x={9} y={9} width={11} height={11} rx={2} /><path d="M5 15V6a2 2 0 0 1 2-2h9" /></>);

export const DollarIcon = (p: IconProps) =>
  base(
    p,
    <>
      <circle cx={12} cy={12} r={9} />
      <path d="M12 6.5v11" />
      <path d="M15 9.3c0-1.5-1.4-2.3-3-2.3s-3 .9-3 2.3c0 1.4 1.4 1.9 3 2.3s3 1 3 2.4-1.4 2.3-3 2.3-3-.8-3-2.3" />
    </>
  );

// Footer social marks (simplified line-art, not exact brand logos).
export const SocialXIcon = (p: IconProps) =>
  base(p, <><line x1={5} y1={5} x2={19} y2={19} /><line x1={19} y1={5} x2={5} y2={19} /></>);

export const SocialDiscordIcon = (p: IconProps) =>
  base(p, <><path d="M7 8.2C7 6 9 5 12 5s5 1 5 3.2V13c0 3-2.2 5-5 5s-5-2-5-5Z" /><circle cx={9.5} cy={10.5} r={0.6} fill="currentColor" stroke="none" /><circle cx={14.5} cy={10.5} r={0.6} fill="currentColor" stroke="none" /></>);

export const SocialTelegramIcon = (p: IconProps) =>
  base(p, <path d="M21 4 3 11l6 2 2 6 3-4 5 3 2-14Z" />);

export const SocialGithubIcon = (p: IconProps) =>
  base(p, <><circle cx={12} cy={12} r={9} /><path d="M9.5 18.5c-3.5 1-3.5-1.5-5-1.5" /><path d="M9.5 18.5v-2.1c-3-.3-3.7-1.7-3.7-3.4 0-1 .3-1.8.9-2.4-.1-.3-.4-1.3.1-2.6 0 0 .9-.3 2.7.9.8-.2 1.6-.3 2.5-.3s1.7.1 2.5.3c1.8-1.2 2.7-.9 2.7-.9.5 1.3.2 2.3.1 2.6.6.6.9 1.4.9 2.4 0 1.7-.7 3.1-3.7 3.4v2.1" /></>);
